import { Avatar, Button, Input, Modal } from "@deligoo/ui"
import { ChangeEvent, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"

import { globalStore } from "@/App"
import { Driver, Order } from "@/types"
import { DriverUtils, escapeRegex, OrderUtils } from "@/utils"
import { handleStandardError } from "@/utils/errors"
import { fetchDriversForReassignment } from "@/utils/fetch"

import cls from "../OrderContextMenu.module.scss"

type SelectDriverModalProps = {
  orderId: Order["id"]
  setSelectedDriver: (driver: Driver) => void
  handleClose: () => void
}

export const SelectDriverModal = ({ setSelectedDriver, handleClose, orderId }: SelectDriverModalProps) => {
  const { t } = useTranslation()

  const [searchValue, setSearchValue] = useState("")
  const [driversForReassignment, setDriversForReassignment] = useState<Driver[] | null>(null)

  useEffect(() => {
    const getDriversForReassignment = async () => {
      try {
        const { response, json } = await fetchDriversForReassignment(orderId)
        if (response.ok && json.data) {
          const sortedDriversByNames = json.data.sort((a: Driver, b: Driver) => a.name.localeCompare(b.name, "pl"))
          setDriversForReassignment(sortedDriversByNames)
        } else {
          handleStandardError(response, json)
        }
      } catch (err) {
        toast.error(t("drivers:reassign_driver_fetch_fail"))
      }
    }
    getDriversForReassignment()
  }, [t, orderId])

  const filteredDriversForReassignment = useMemo(() => {
    return driversForReassignment?.filter(
      (driver) =>
        filterBySearch(driver, searchValue) || searchValue === getDriverCurrentOrdersCount(driver.id).toString()
    )
  }, [searchValue, driversForReassignment])

  return (
    <Modal handleClose={handleClose} className={cls.selectDriverModal} closeBtnPosition="inside">
      <Modal.Content>
        <div className="h300 mb-8">Dopasuj kuriera</div>

        <Input
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.currentTarget.value)}
          placeholder="Wyszukaj kuriera"
          iconRightProps={{ name: "search", color: "muted" }}
          autoFocus
          plain
        />
      </Modal.Content>

      <div className={cls.driversList}>
        {filteredDriversForReassignment ? (
          filteredDriversForReassignment.map((driver) => (
            <Button
              onClick={() => {
                setSelectedDriver(driver)
                handleClose()
              }}
              className={cls.proposedDriver}
              variation="outlined"
              color="muted"
              key={driver.id}
              fullWidth
            >
              <div className={cls.proposedDriverContent}>
                <Avatar
                  className="mr-5"
                  color={DriverUtils.getAvatarColor(driver)}
                  indicator={DriverUtils.getAvatarIndicator(driver)}
                  dotColor={DriverUtils.getAvatarDotColor(driver)}
                >
                  {driver.name}
                </Avatar>
                {`${driver.name} (${getDriverCurrentOrdersCount(driver.id)}) ${
                  driver.updated_working_hours || driver.working_hours
                }`}
              </div>
            </Button>
          ))
        ) : (
          <div className="text-center text-muted p-5 m-auto">Brak aktywnych kurierów</div>
        )}
      </div>
    </Modal>
  )
}

function getDriverCurrentOrdersCount(driverId: Driver["id"]) {
  const { entities: ordersEntities } = globalStore.getState().orders
  return Object.values(ordersEntities).filter(
    (order) => order && OrderUtils.isAssigned(order) && order.driver_id === driverId
  ).length
}

function filterBySearch(driver: Driver, searchValue: string) {
  if (searchValue) {
    const searchValues = escapeRegex(searchValue).trim().split(" ")
    const regExps = searchValues.map((searchValue) => new RegExp(searchValue, "i"))
    return regExps.every((regex) => regex.test(driver.name))
  }
  return true
}
